import { RouteRecordRaw } from 'vue-router';

import OnboardingSoleTraderLayout from '@/modules/onboarding-dashboard/SoleTrader/components/OnboardingSoleTraderLayout.vue';
import SoleTraderProfile from '@/modules/onboarding-dashboard/SoleTrader/pages/SoleTraderProfile.vue';
import { RoutePermission } from '@/router/route-permission';
import { useOnboardingAdminSoleTraderStore } from '@/stores/onboarding/OnboardingAdminSoleTrader';

const PersonalProfileWallets = () => import('@/modules/onboarding-user-profile/pages/user-profile-wallets/UserProfileWallets.vue');

const AccountStatus = () => import('@/modules/onboarding-user-profile/pages/user-account-status/user-account-status.vue');
const ProfileTransactions = () => import('@/modules/transactions-old/pages/TransactionsPage.vue');
const ProfileLoginAndSecurity = () => import('@/modules/onboarding-user-profile/pages/user-login-and-security/user-login-and-security.vue');
const UserPlan = () => import('@/modules/onboarding-user-profile/pages/user-plan/UserPlan.vue');
const ProfileSubscriptions = () => import('@/modules/user-profile/pages/user-subscriptions/index.vue');
const AccountNavigation = () => import('@/shared-components/account-nav.vue');

const ONBOARDING_SOLE_TRADER_PROFILE_META = {
  groupName: 'onboarding-sole-trader-profile',
  layout: {
    props: {
      backName: [
        'onboarding-users',
      ],
    },
  },
};

export const ONBOARDING_SOLE_TRADER_ACCOUNT_TAB_CHILDREN: RouteRecordRaw[] = [
  {
    path: 'security',
    name: 'onboarding-sole-trader-account-login-and-security',
    component: ProfileLoginAndSecurity,
    props: () => ({
      userDetails: useOnboardingAdminSoleTraderStore().userDetails,
      updateUserContacts: useOnboardingAdminSoleTraderStore().updateUserContacts,
      applicationType: 'personal',
      disabled: useOnboardingAdminSoleTraderStore().applicationStatus === 'CLOSED',
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.security',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'status',
    name: 'onboarding-sole-trader-account-status',
    component: AccountStatus,
    props: () => ({
      applicationStatus: useOnboardingAdminSoleTraderStore().applicationStatus,
      blocked: useOnboardingAdminSoleTraderStore().blocked,
      toggleBlockStatus: useOnboardingAdminSoleTraderStore().toggleBlockStatus,
      banned: useOnboardingAdminSoleTraderStore().banned,
      unban: useOnboardingAdminSoleTraderStore().unban,
      close: useOnboardingAdminSoleTraderStore().close,
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.account_blocking',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
];

export const ONBOARDING_SOLE_TRADER_PROFILE_CHILDREN: RouteRecordRaw[] = [
  {
    path: 'profile',
    name: 'onboarding-sole-trader-details',
    component: SoleTraderProfile,
    meta: {
      translation: 'pages.onboarding.admin_page.profile',
      icon: 'icon-user-onboarding',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'wallets',
    name: 'onboarding-sole-trader-wallets',
    component: PersonalProfileWallets,
    props: () => ({
      organizationId: useOnboardingAdminSoleTraderStore().userDetails?.organizationId,
    }),
    meta: {
      translation: 'pages.user_profile.navigation.wallets',
      icon: 'icon-wallet',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'transactions',
    name: 'onboarding-sole-trader-transactions',
    component: ProfileTransactions,
    props: () => ({
      defaultFilter: {
        orgIds: [useOnboardingAdminSoleTraderStore().userDetails?.organizationId],
      },
      isTitleVisible: false,
      isFullPage: false,
      isAddCharge: useOnboardingAdminSoleTraderStore().applicationStatus === 'VERIFIED',
    }),
    meta: {
      translation: 'pages.user_profile.navigation.transactions',
      icon: 'icon-list',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'plan',
    name: 'onboarding-sole-trader-plan',
    component: UserPlan,
    props: () => ({
      userId: useOnboardingAdminSoleTraderStore().userDetails?.userId,
      role: useOnboardingAdminSoleTraderStore().userDetails?.role,
      customerType: 'SOLE_TRADER',
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.plan',
      icon: 'icon-coins',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'subscriptions',
    name: 'onboarding-sole-trader-subscriptions',
    component: ProfileSubscriptions,
    props: () => ({
      organizationId: useOnboardingAdminSoleTraderStore().userDetails?.organizationId,
      disabled: useOnboardingAdminSoleTraderStore().applicationStatus === 'CLOSED',
    }),
    meta: {
      translation: 'pages.contracts.navigation.subscriptions',
      icon: 'icon-card-thin',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
  {
    path: 'account',
    name: 'onboarding-sole-trader-account',
    component: AccountNavigation,
    props: () => ({
      userId: useOnboardingAdminSoleTraderStore().userDetails?.userId,
      tabs: ONBOARDING_SOLE_TRADER_ACCOUNT_TAB_CHILDREN,
    }),
    children: ONBOARDING_SOLE_TRADER_ACCOUNT_TAB_CHILDREN,
    redirect: ONBOARDING_SOLE_TRADER_ACCOUNT_TAB_CHILDREN[0],
    meta: {
      translation: 'pages.onboarding.admin_page.account',
      icon: 'icon-lock',
      ...ONBOARDING_SOLE_TRADER_PROFILE_META,
    },
  },
];

export const ONBOARDING_SOLE_TRADER: RouteRecordRaw = {
  path: '/dashboard/customers/sole-trader/:id',
  name: ONBOARDING_SOLE_TRADER_PROFILE_META.groupName,
  children: ONBOARDING_SOLE_TRADER_PROFILE_CHILDREN,
  component: OnboardingSoleTraderLayout,
  redirect: (to) => ({
    name: ONBOARDING_SOLE_TRADER_PROFILE_CHILDREN[0].name,
    params: to.params,
  }),
  meta: {
    permission: RoutePermission.ONBOARDING_USERS,
    root: false,
    ...ONBOARDING_SOLE_TRADER_PROFILE_META,
  },
};
